import { createContext, useContext } from 'react';
import { z } from 'zod';
import { componentSchema } from '@/config/schema';
import { useStableIds } from '@/hooks/useStableIds';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';
import { WidgetGrid } from '@/widgets/WidgetGrid';
import { distributeSpans } from '@/widgets/widgetSpan';

/**
 * A container widget: renders `options.components` — the same node shape as the top-level
 * `components` array — as equal-width columns of the shared 12-column grid. Port of legacy
 * `groupWidget.vue`.
 *
 * It is a registered widget like any other, which is what lets it nest: a `GroupWidget`'s
 * `options.components` may itself contain a `GroupWidget`.
 *
 * No deployed config uses it today. It is ported because the legacy app registered it and a
 * config can start using it without a code change.
 */

/**
 * `options.components` — an array of ordinary component nodes.
 *
 * Reuses `componentSchema` rather than redeclaring the node shape, so a change to the config
 * format cannot leave nested components parsing by different rules than top-level ones.
 */
const groupOptionsSchema = z.object({
  components: z.array(componentSchema),
});

/**
 * How many `GroupWidget`s deep we are. Top-level widgets sit at 0.
 *
 * The cap exists for a config-authoring mistake, not for any config that exists: a group
 * whose `options.components` contains itself would recurse until the tab dies, and on a
 * wall-mounted display that is indistinguishable from a hardware fault. Three levels is far
 * past anything the layout could usefully express.
 */
export const MAX_GROUP_DEPTH = 3;

const GroupDepthContext = createContext(0);

export function GroupWidget({ componentId, options }: WidgetProps) {
  const depth = useContext(GroupDepthContext);
  const getId = useStableIds();
  const parsed = groupOptionsSchema.safeParse(options);

  if (depth >= MAX_GROUP_DEPTH) {
    warnOnce(
      `group-depth:${componentId}`,
      '[signage] GroupWidget nested past the depth limit of %d; rendering nothing below it. ' +
        'This is a config authoring mistake — check `options.components` for a group that ' +
        'contains itself.',
      MAX_GROUP_DEPTH,
    );
    return null;
  }

  // A malformed group costs its own subtree, never the page (CLAUDE.md constraint 4). Legacy
  // was laxer — `Array.isArray(props.options.components)` and no per-node checking — so a
  // node missing `type` reached Vue's dynamic component resolver as `undefined`. Here it
  // fails the schema and the whole group is skipped, with the reason in the console.
  if (!parsed.success) {
    warnOnce(
      `group-options:${componentId}`,
      '[signage] GroupWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const children = parsed.data.components;
  const equalShares = distributeSpans(children.length);

  return (
    <GroupDepthContext value={depth + 1}>
      {/*
       * Legacy `.group-container` / `.group-container-item`, whose SCSS was: column by
       * default, and from `lg` a row with `gap: 1rem` and `flex: 1 1 0` + `width: 0` per item.
       *
       * That is `span 12/n` on each child, so it is expressed on the shared grid instead of as
       * a second layout model beside it. `flex-1 lg:w-0` is gone with the flex row it belonged
       * to; the zero width it forced — without which a long text widget claims more than its
       * share, because `flex-basis: 0` still leaves `width: auto` as the min-content floor —
       * is `minmax(0, 1fr)` plus `min-width: 0` on the grid, which is the same fix with a
       * wider blast radius.
       *
       * Two consequences worth stating, neither of which any deployed config can observe (no
       * config in either repository uses `GroupWidget`):
       *
       *   - **The gutter is the app's gutter.** `--widget-gutter` is 1.5rem from `lg` up
       *     rather than the group's own 1rem, because there is now one gutter in the app.
       *   - **A child may override its share.** A child carrying its own `span` gets it, so a
       *     group is also the way to put a 2/10 split inside one row of the outer grid.
       *
       * The two legacy class names are kept as markers with no CSS attached — they cost
       * nothing and they make a DOM diff against the legacy app (SYM-506) readable.
       */}
      <WidgetGrid
        className="group-container"
        cellClassName="group-container-item"
        items={children.map((component, index) => ({
          component,
          componentId: getId(index),
          span: component.span ?? equalShares[index],
        }))}
      />
    </GroupDepthContext>
  );
}
